import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../../../utils/api';
import { getProjectFamilyIds } from '../utils/projectShareUtils';
import ModalPortal from '../../../components/ModalPortal';

function getFamilyId(family) {
    return family?.id || family?.Id;
}

function getFamilyName(family) {
    return family?.name || family?.Name || '';
}

function getFamilyUpdatedAt(family) {
    return family?.updatedAt || family?.UpdatedAt || family?.createdAt || family?.CreatedAt;
}

function formatUpdatedDate(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString();
}

function shortFamilyId(familyId) {
    return String(familyId || '').replace(/-/g, '').slice(0, 8);
}

export default function LinkFamilyModal({ isOpen, onClose, selectedProject, onLink }) {
    const overlayMouseDown = useRef(false);
    const [families, setFamilies] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) setSelectedIds([]);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            try {
                const res = await apiFetch(`/api/products?t=${Date.now()}`);
                if (res.ok && !cancelled) {
                    setFamilies(await res.json());
                }
            } catch (error) {
                console.error('Failed to load families', error);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [isOpen]);

    const linkedIds = useMemo(() => getProjectFamilyIds(selectedProject).map(id => String(id)), [selectedProject]);

    const candidates = useMemo(() => {
        return families.filter(family => {
            const familyId = String(getFamilyId(family));
            const parentId = family.parentId || family.ParentId
                ? String(family.parentId || family.ParentId)
                : null;
            return !linkedIds.includes(familyId) && !(parentId && linkedIds.includes(parentId));
        });
    }, [families, linkedIds]);

    const duplicateNames = useMemo(() => {
        const counts = new Map();
        candidates.forEach(family => {
            const name = getFamilyName(family);
            counts.set(name, (counts.get(name) || 0) + 1);
        });
        return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([name]) => name));
    }, [candidates]);

    if (!isOpen) return null;

    const toggleFamily = (familyId) => {
        setSelectedIds(prev => prev.includes(familyId)
            ? prev.filter(id => id !== familyId)
            : [...prev, familyId]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedIds.length === 0) return;
        setIsSubmitting(true);
        try {
            await onLink(selectedIds);
            setSelectedIds([]);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ModalPortal>
        <div
            className="modal-overlay"
            onMouseDown={(e) => { if (e.target === e.currentTarget) overlayMouseDown.current = true; }}
            onMouseUp={(e) => {
                if (e.target === e.currentTarget && overlayMouseDown.current) onClose();
                overlayMouseDown.current = false;
            }}
        >
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ minWidth: '400px' }}>
                <h2 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>Link existing family</h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.25rem', fontSize: '0.9rem' }}>
                    Attach an existing family to this project. Shared families stay the same model in every project.
                </p>
                <form onSubmit={handleSubmit} className="importer-form">
                    {isLoading ? (
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Loading families…</p>
                    ) : candidates.length === 0 ? (
                        <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.85rem' }}>
                            No other families are available to link.
                        </p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '240px', overflowY: 'auto' }}>
                            {candidates.map(family => {
                                const familyId = getFamilyId(family);
                                const name = getFamilyName(family);
                                const updated = formatUpdatedDate(getFamilyUpdatedAt(family));
                                const showId = duplicateNames.has(name);
                                return (
                                    <label key={familyId} style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '0.6rem',
                                        padding: '0.5rem 0.75rem',
                                        background: 'var(--bg-secondary)',
                                        border: '1px solid var(--border)',
                                        borderRadius: 'var(--radius)',
                                        cursor: 'pointer'
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(familyId)}
                                            onChange={() => toggleFamily(familyId)}
                                            style={{ marginTop: '0.2rem' }}
                                        />
                                        <span style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', minWidth: 0 }}>
                                            <span style={{ fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-word' }}>
                                                {showId ? `${name} (${shortFamilyId(familyId)})` : name}
                                            </span>
                                            {updated && (
                                                <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                                                    Updated {updated}
                                                </span>
                                            )}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                    <div className="modal-actions">
                        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={selectedIds.length === 0 || isSubmitting}
                            style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)' }}
                        >
                            {isSubmitting ? 'Linking…' : 'Link'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
        </ModalPortal>
    );
}
